'use client';
import { EditorContent, type Extension } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { useEditorWithExtensions } from '@/app/hooks/editor';
import { useStore } from '@/app/providers/store';
import type { ElementModality, MentionItem, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { cn, extractMentionsIdsFromEditor } from '@/app/utils';
import { MentionContext } from './context';
import { useCustomMention } from './hooks/use-custom-mention';
import { renderPlaceholderWithAtButton } from './render-placeholder-with-at-button';

/** E2E：挂在 TipTap 根节点（contenteditable）上，页面级通常仅一处 */
const PROMPT_TEXT_EDITOR_TEST_ID = 'prompt-text-editor';
const PROMPT_TEXT_EDITOR_MIN_HEIGHT = 116;
const PROMPT_TEXT_EDITOR_MAX_HEIGHT = 320;
const PROMPT_TEXT_EDITOR_ACTION_HEIGHT = 24;

const nextFrame = (callback: () => void) => {
  requestAnimationFrame(() => requestAnimationFrame(callback));
};

interface MentionsTextareaProps {
  className?: string;
  textPrompt: TextPromptWithInputItem;
  updateTextPrompt: (value: TextPromptWithInputItem) => void;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  onClear?: () => void;
  /** 获取额外追加的元素，用于显示在@提示框中 */
  getExtraItems?: () => ReferencePrompt[];
  /** 获取 mention 的 prefix（React node） */
  getPrefix?: (mentionId: string, label: string) => React.ReactNode | null;
  /** 占位符文本 */
  placeholder?: string;
  /** 允许的 modality 筛选 */
  allowedModalities?: Array<ElementModality>;
  allowClear?: boolean;
  fitContainer?: boolean;
  minHeight?: number;
  maxHeight?: number;
  /**
   * placeholder 中的 "@" 字符改为可点击按钮，点击后触发此回调（如打开主体选择弹窗）。
   * 未传时 "@" 保持纯文本，随 placeholder 整体透传点击到编辑器。
   */
  onAtButtonClick?: () => void;
}

export const MentionsTextarea: FC<MentionsTextareaProps> = ({
  className,
  textPrompt,
  updateTextPrompt,
  onBeforeDeleteMention,
  onBeforeAddMention,
  onClear,
  getExtraItems,
  getPrefix,
  placeholder,
  allowedModalities,
  allowClear = true,
  fitContainer = false,
  minHeight = PROMPT_TEXT_EDITOR_MIN_HEIGHT,
  maxHeight = PROMPT_TEXT_EDITOR_MAX_HEIGHT,
  onAtButtonClick,
}) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [editorContainerHeight, setEditorContainerHeight] = useState(PROMPT_TEXT_EDITOR_MIN_HEIGHT);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const previousMentionsIds = useRef<string[]>([]);
  const { mentionExtension, onUpdateCallback } = useCustomMention({
    previousMentionsIds,
    onBeforeAddMention,
    onBeforeDeleteMention,
    getExtraItems,
    allowedModalities,
    enableRemoteSearch: !!userInfo,
  });

  const mentionContextValue = useMemo(() => ({ getPrefix }), [getPrefix]);

  const { editor, isEmpty } = useEditorWithExtensions({
    content: textPrompt,
    updateContent: updateTextPrompt,
    maxLength: MAX_PROMPT_LENGTH,
    onMaxLengthExceeded: () => {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: MAX_PROMPT_LENGTH }),
      });
    },
    extensions: [mentionExtension as Extension],
    editorProps: {
      attributes: {
        class: 'min-h-[112px]',
        'data-testid': PROMPT_TEXT_EDITOR_TEST_ID,
      },
    },
    onUpdateCallback,
  });

  // 不管内部更新（editor变化）还是外部更新（textPrompt变化）都更新 previousMentionsIds
  useEffect(() => {
    if (editor) {
      // textPrompt 变化后，经过 setTimeout 才更新 editor，所以需要 setTimeout 来获取正确的 previousMentionsIds
      setTimeout(() => {
        previousMentionsIds.current = extractMentionsIdsFromEditor(editor);
      }, 0);
    }
  }, [editor, textPrompt]);

  const updateEditorContainerHeight = () => {
    const editorDom = editor?.view.dom;
    if (!editorDom) return;

    const effectiveMaxHeight = maxHeight ?? (fitContainer ? Number.POSITIVE_INFINITY : PROMPT_TEXT_EDITOR_MAX_HEIGHT);

    const contentHeight = editorDom.scrollHeight + PROMPT_TEXT_EDITOR_ACTION_HEIGHT;
    if (fitContainer) {
      const rootElement = rootRef.current;
      const panel = rootElement?.closest('[data-create-input-panel="true"]');
      const panelMaxHeight = panel ? Number.parseFloat(window.getComputedStyle(panel).maxHeight) : Number.NaN;
      const maxTextareaHeight =
        panel && rootElement && Number.isFinite(panelMaxHeight)
          ? Math.max(
              Math.min(effectiveMaxHeight, panelMaxHeight - (panel.scrollHeight - rootElement.clientHeight)),
              minHeight,
            )
          : effectiveMaxHeight;
      const targetHeight = isEmpty ? minHeight : contentHeight;
      setEditorContainerHeight(Math.min(targetHeight, maxTextareaHeight));
      return;
    }
    setEditorContainerHeight(Math.min(Math.max(contentHeight, minHeight), effectiveMaxHeight));
  };

  useLayoutEffect(() => {
    updateEditorContainerHeight();
  }, [availableHeight, editor, fitContainer, maxHeight, minHeight, textPrompt]);

  useLayoutEffect(() => {
    if (!fitContainer || !rootRef.current?.parentElement) return;

    const parentElement = rootRef.current.parentElement;
    const panel = rootRef.current.closest('[data-create-input-panel="true"]');
    const updateAvailableHeight = () => {
      setAvailableHeight(Math.max(parentElement.clientHeight, minHeight));

      if (!panel) {
        return;
      }
    };

    updateAvailableHeight();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(updateAvailableHeight);
    resizeObserver.observe(parentElement);
    if (panel) {
      resizeObserver.observe(panel);
    }

    return () => resizeObserver.disconnect();
  }, [fitContainer, minHeight]);

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative flex w-full flex-col overflow-hidden text-sm leading-6 sm:text-sm lg:text-xs lg:leading-5',
        className,
      )}
      style={
        fitContainer
          ? {
              flex: '1 0 auto',
              minHeight,
              height: editorContainerHeight,
              ...(maxHeight === undefined ? {} : { maxHeight }),
            }
          : { height: editorContainerHeight, ...(maxHeight === undefined ? {} : { maxHeight }) }
      }
      onClick={() => {
        editor?.commands.focus();
      }}
    >
      <div className="absolute inset-0 bottom-6 overflow-auto">
        <MentionContext.Provider value={mentionContextValue}>
          <EditorContent
            editor={editor}
            className="h-full w-full rounded-none border-none bg-transparent p-0 text-sm text-system-white leading-6 caret-system-blue02 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm lg:text-xs lg:leading-5"
          />
        </MentionContext.Provider>
      </div>

      {isEmpty ? (
        // 显示 placeholder；若提供 onAtButtonClick，把文案里第一个 "@" 拆成可点击按钮
        <div className="pointer-events-none absolute inset-0 select-none items-center text-system-white36">
          {renderPlaceholderWithAtButton(
            placeholder || t('create.char2VideoForm.input.placeholder.noGuide'),
            onAtButtonClick,
          )}
        </div>
      ) : (
        // 显示清空按钮
        allowClear && (
          <div className="absolute right-0 bottom-0">
            <BaseLineDelete
              className="cursor-pointer text-base"
              onClick={() => {
                onClear?.();
                nextFrame(updateEditorContainerHeight);
              }}
            />
          </div>
        )
      )}
    </div>
  );
};
